import { useEffect, useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import { useGetGemsExchangeRateActiveQuery, useGetGemsRewardAttemptsQuery } from "../../../../redux/apiSlice";
import { Card, CardBody, CardHeader } from "../../../../_metronic/_partials/controls";
import { SubHeader } from "../../Common/layout/SubHeader";
import { Modal } from "react-bootstrap";
import { formatDate, formatStatus, formatUsername, truncateString } from "../../../util/Helpers";
import { rewardTypeOptions } from "../constants/rewardTypes";

const todayStr = new Date().toISOString().split("T")[0];
const PAGE_SIZE = 10;
const formatMoney = (currency, value) => {
    const numericValue = Number(value);
    if (!Number.isFinite(numericValue)) return "—";
    return `${currency || "NGN"} ${numericValue.toLocaleString(undefined, { maximumFractionDigits: 2 })}`;
};

const GemsRewardAttempts = () => {
    const { userGroup } = useParams();
    const [sortBy, setSortBy] = useState("created");
    const [selectedAttempt, setSelectedAttempt] = useState(null);

    const [filters, setFilters] = useState({
        startDate: "",
        endDate: todayStr,
        status: "",
        rewardType: "",
    });
    const [page, setPage] = useState(1);

    const args = useMemo(
        () => ({
            userGroup,
            startDate: filters.startDate || "",
            endDate: filters.endDate || "",
            status: filters.status || "",
            rewardType: filters.rewardType || "",
            limit: 1000,
        }),
        [userGroup, filters.startDate, filters.endDate, filters.status, filters.rewardType]
    );

    const { data: rewardAttempts, isFetching } = useGetGemsRewardAttemptsQuery(args);
    const { data: activeExchangeRate } = useGetGemsExchangeRateActiveQuery({ userGroup }, { skip: !userGroup });
    const activeRate = activeExchangeRate?.data || activeExchangeRate || null;
    const activeRateValue = Number(activeRate?.rate ?? 0);
    const activeRateCurrency = activeRate?.currency || "NGN";

    const attempts = useMemo(() => {
        const payload = rewardAttempts?.data ?? rewardAttempts ?? null;
        if (Array.isArray(payload)) return payload;
        if (Array.isArray(payload?.rows)) return payload.rows;
        if (Array.isArray(payload?.data)) return payload.data;
        if (Array.isArray(rewardAttempts?.rows)) return rewardAttempts.rows;
        return [];
    }, [rewardAttempts]);
    const sortedAttempts = useMemo(() => {
        const list = [...attempts];
        if (sortBy === "created") {
            return list.sort((a, b) => new Date(b.created).getTime() - new Date(a.created).getTime());
        }
        return list.sort((a, b) => {
            const aName = (a.username || a.userId || "").toLowerCase();
            const bName = (b.username || b.userId || "").toLowerCase();
            if (aName < bName) return -1;
            if (aName > bName) return 1;
            return new Date(b.created).getTime() - new Date(a.created).getTime();
        });
    }, [attempts, sortBy]);
    const totalPages = useMemo(
        () => Math.max(1, Math.ceil(sortedAttempts.length / PAGE_SIZE)),
        [sortedAttempts.length]
    );
    const pagedAttempts = useMemo(() => {
        const start = (Math.max(1, page) - 1) * PAGE_SIZE;
        return sortedAttempts.slice(start, start + PAGE_SIZE);
    }, [page, sortedAttempts]);

    useEffect(() => {
        setPage(1);
    }, [filters.startDate, filters.endDate, filters.status, filters.rewardType, sortBy, userGroup]);

    useEffect(() => {
        if (page > totalPages) setPage(totalPages);
    }, [page, totalPages]);

    const onFilterChange = (e) => {
        const { name, value } = e.target;
        setFilters((prev) => ({ ...prev, [name]: value }));
    };

    const getReasonText = (attempt) =>
        attempt?.reason ||
        attempt?.failureReason ||
        attempt?.error ||
        attempt?.message ||
        attempt?.statusReason ||
        "-";

    const getMetaValue = (attempt) => {
        const metaCandidate = attempt?.meta ?? attempt?.metadata ?? attempt?.metaData ?? null;
        if (!metaCandidate) return null;
        if (typeof metaCandidate !== "string") return metaCandidate;

        try {
            return JSON.parse(metaCandidate);
        } catch (e) {
            return metaCandidate;
        }
    };

    const getMetaPrettyText = (attempt) => {
        const metaValue = getMetaValue(attempt);
        if (!metaValue) return "-";
        if (typeof metaValue === "string") return metaValue;
        try {
            return JSON.stringify(metaValue, null, 2);
        } catch (e) {
            return String(metaValue);
        }
    };

    return (
        <>
            {userGroup && (
                <SubHeader>
                    <SubHeader.Nav>
                        <SubHeader.NavItem label={"Overview"} to={`/gems/${userGroup}/overview`} />
                        <SubHeader.NavItem label={"Accruals"} to={`/gems/${userGroup}/overview/accruals`} />
                        <SubHeader.NavItem label={"Redemptions"} to={`/gems/${userGroup}/overview/redemptions`} />
                        <SubHeader.NavItem label={"Campaigns"} to={`/gems/${userGroup}/overview/campaigns`} />
                        <SubHeader.NavItem
                            label={"Reward attempts"}
                            to={`/gems/${userGroup}/overview/reward-attempts`}
                        />
                        <SubHeader.NavItem label={"Audit logs"} to={`/gems/${userGroup}/overview/audit-logs`} />
                    </SubHeader.Nav>
                </SubHeader>
            )}
            <div className="py-4 px-6">
                <div className="mb-4">
                    <h2 className="font-weight-bolder mb-1">Reward attempts</h2>
                    <div className="text-muted mb-4">
                        Review failed and successful reward attempts with reasons and metadata.
                    </div>
                    <div className="d-flex flex-wrap align-items-end">
                        <div className="mr-3 mb-2">
                            <label className="text-muted font-size-sm mb-1">Start date</label>
                            <input
                                type="date"
                                className="form-control"
                                name="startDate"
                                value={filters.startDate}
                                onChange={onFilterChange}
                            />
                        </div>
                        <div className="mr-3 mb-2">
                            <label className="text-muted font-size-sm mb-1">End date</label>
                            <input
                                type="date"
                                className="form-control"
                                name="endDate"
                                value={filters.endDate}
                                onChange={onFilterChange}
                            />
                        </div>
                        <div className="mb-2">
                            <label className="text-muted font-size-sm mb-1">Status</label>
                            <select
                                className="form-control"
                                name="status"
                                value={filters.status}
                                onChange={onFilterChange}
                            >
                                <option value="">All</option>
                                <option value="failed">Failed</option>
                                <option value="completed">Completed</option>
                            </select>
                        </div>
                        <div className="ml-3 mb-2">
                            <label className="text-muted font-size-sm mb-1">Reward type</label>
                            <select
                                className="form-control"
                                name="rewardType"
                                value={filters.rewardType}
                                onChange={onFilterChange}
                            >
                                <option value="">All</option>
                                {rewardTypeOptions.map((rewardType) => (
                                    <option key={rewardType} value={rewardType}>
                                        {rewardType}
                                    </option>
                                ))}
                            </select>
                        </div>
                        <div className="ml-3 mb-2">
                            <label className="text-muted font-size-sm mb-1">Sort</label>
                            <select
                                className="form-control"
                                name="sortBy"
                                value={sortBy}
                                onChange={(e) => setSortBy(e.target.value)}
                            >
                                <option value="created">Created</option>
                                <option value="user">User</option>
                            </select>
                        </div>
                    </div>
                </div>

                <Card>
                    <CardHeader title="Reward attempts" />
                    <CardBody>
                        <div className="table-responsive">
                            <table className="table table-sm table-head-custom">
                                <thead>
                                    <tr>
                                        <th>Created</th>
                                        <th>User</th>
                                        <th>Type</th>
                                        <th className="text-right">Amount</th>
                                        <th className="text-right">Value</th>
                                        <th>Status</th>
                                        <th>Reason</th>
                                        <th className="text-right" style={{ width: 80 }}></th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {isFetching ? (
                                        <tr>
                                            <td colSpan={8} className="text-center text-muted py-5">
                                                Loading...
                                            </td>
                                        </tr>
                                    ) : pagedAttempts.length ? (
                                        pagedAttempts.map((a) => {
                                            const amount = Number(a.amount || 0);
                                            const cashValue = Number.isFinite(activeRateValue) && activeRateValue > 0 ? amount * activeRateValue : null;
                                            return (
                                            <tr key={a.id}>
                                                <td>{formatDate(a.created)}</td>
                                                <td>
                                                    {a.userId
                                                        ? formatUsername(a.userId, a.username || a.userId, "", null, "gems")
                                                        : "-"}
                                                </td>
                                                <td>{a.rewardType}</td>
                                                <td className="text-right font-weight-bolder">
                                                    {amount.toLocaleString()}
                                                </td>
                                                <td className="text-right text-muted">
                                                    {cashValue !== null ? formatMoney(activeRateCurrency, cashValue) : "—"}
                                                </td>
                                                <td>{formatStatus(a.status)}</td>
                                                <td style={{ minWidth: 240 }} title={getReasonText(a)}>
                                                    {truncateString(getReasonText(a), 180) || "-"}
                                                </td>
                                                <td className="text-right">
                                                    <button
                                                        type="button"
                                                        className="btn btn-icon btn-light btn-hover-primary btn-sm"
                                                        onClick={() => setSelectedAttempt(a)}
                                                        title="View details"
                                                    >
                                                        <i className="fa fa-eye" />
                                                    </button>
                                                </td>
                                            </tr>
                                        );
                                        })
                                    ) : (
                                        <tr>
                                            <td colSpan={8} className="text-center text-muted py-5">
                                                No reward attempts for selected filters
                                            </td>
                                        </tr>
                                    )}
                                </tbody>
                            </table>
                        </div>
                        {!isFetching && sortedAttempts.length > 0 && (
                            <div className="d-flex justify-content-between align-items-center mt-4">
                                <div className="text-muted font-size-sm">
                                    Showing {((page - 1) * PAGE_SIZE) + 1}-{Math.min(page * PAGE_SIZE, sortedAttempts.length)} of {sortedAttempts.length}
                                </div>
                                <div>
                                    <button
                                        type="button"
                                        className="btn btn-light mr-2"
                                        onClick={() => setPage((prev) => Math.max(1, prev - 1))}
                                        disabled={page <= 1}
                                    >
                                        Previous
                                    </button>
                                    <button
                                        type="button"
                                        className="btn btn-primary"
                                        onClick={() => setPage((prev) => Math.min(totalPages, prev + 1))}
                                        disabled={page >= totalPages}
                                    >
                                        Next
                                    </button>
                                </div>
                            </div>
                        )}
                    </CardBody>
                </Card>
            </div>

            <Modal
                show={!!selectedAttempt}
                onHide={() => setSelectedAttempt(null)}
                aria-labelledby="gems-reward-attempt-details-modal"
                centered
            >
                <Modal.Header>
                    <Modal.Title id="gems-reward-attempt-details-modal">Reward attempt details</Modal.Title>
                    <button type="button" className="close" onClick={() => setSelectedAttempt(null)}>
                        <span aria-hidden="true" className="d-block">
                            ×
                        </span>
                        <span className="sr-only">Close</span>
                    </button>
                </Modal.Header>
                <Modal.Body style={{ maxHeight: "calc(100vh - 300px)", overflow: "auto" }}>
                    {selectedAttempt ? (
                        <>
                            <div className="row mb-4">
                                <div className="col-6 mb-4">
                                    <div className="text-muted font-size-xs text-uppercase mb-1">Created</div>
                                    <div className="font-weight-bolder">{formatDate(selectedAttempt.created)}</div>
                                </div>
                                <div className="col-6 mb-4">
                                    <div className="text-muted font-size-xs text-uppercase mb-1">Status</div>
                                    <div>{formatStatus(selectedAttempt.status)}</div>
                                </div>
                                <div className="col-6 mb-4">
                                    <div className="text-muted font-size-xs text-uppercase mb-1">Reward type</div>
                                    <div className="font-weight-bolder">{selectedAttempt.rewardType || "-"}</div>
                                </div>
                                <div className="col-6 mb-4">
                                    <div className="text-muted font-size-xs text-uppercase mb-1">Amount</div>
                                    <div className="font-weight-bolder">{Number(selectedAttempt.amount || 0).toLocaleString()} Gems</div>
                                </div>
                                <div className="col-12">
                                    <div className="text-muted font-size-xs text-uppercase mb-1">User</div>
                                    <div className="font-weight-bolder">
                                        {selectedAttempt.userId
                                            ? formatUsername(
                                                  selectedAttempt.userId,
                                                  selectedAttempt.username || selectedAttempt.userId,
                                                  "",
                                                  null,
                                                  "gems"
                                              )
                                            : "-"}
                                    </div>
                                </div>
                            </div>

                            <div className="separator separator-dashed my-4" />

                            <div className="mb-4">
                                <div className="text-muted font-size-xs text-uppercase mb-1">Reason</div>
                                <div className="text-muted" style={{ whiteSpace: "pre-wrap" }}>
                                    {getReasonText(selectedAttempt)}
                                </div>
                            </div>

                            <div className="mb-0">
                                <div className="text-muted font-size-xs text-uppercase mb-1">Meta</div>
                                <pre className="bg-light rounded p-3 mb-0" style={{ whiteSpace: "pre-wrap" }}>
                                    {getMetaPrettyText(selectedAttempt)}
                                </pre>
                            </div>
                        </>
                    ) : null}
                </Modal.Body>
                <Modal.Footer>
                    <button type="button" className="btn btn-light" onClick={() => setSelectedAttempt(null)}>
                        Close
                    </button>
                </Modal.Footer>
            </Modal>
        </>
    );
};

export default GemsRewardAttempts;
